import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Stack,
  Grid,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  Bed as BedIcon,
  SquareFoot as SquareFootIcon,
  AspectRatio as AspectRatioIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  ReceiptLong as ReceiptLongIcon,
  Straighten as StraightenIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Apartment as ApartmentIcon,
} from '@mui/icons-material';
import { renderThumbnail, formatCurrencyWithLabel } from '../../../utils/helper';
import { useBookingDetails } from '../../../contexts/BookingDetailsContext';
import { fetchProjectUnitSelect } from '../../../services/common';
import { useModal } from '../../../contexts/ModalContext';
import { bookingTokens } from './bookingDetailsTheme';

const BookingSidebar = ({ booking, onActionComplete, addProjectUnitDialogOpen: addProjectUnitDialogOpenProp, onOpenAddUnitDialog, onCloseAddUnitDialog }) => {
  const [otherChargesAnchor, setOtherChargesAnchor] = useState(null);
  const [internalDialogOpen, setInternalDialogOpen] = useState(false);
  const isControlled = addProjectUnitDialogOpenProp !== undefined;
  const addProjectUnitDialogOpen = isControlled ? addProjectUnitDialogOpenProp : internalDialogOpen;
  const closeAddUnitDialog = isControlled ? (onCloseAddUnitDialog || (() => {})) : () => setInternalDialogOpen(false);
  const openAddUnitDialog = isControlled ? (onOpenAddUnitDialog || (() => {})) : () => setInternalDialogOpen(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [projectUnits, setProjectUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const { assignProjectUnit } = useBookingDetails();
  const { openSuccessModal, openErrorModal } = useModal();

  // Extract booking data safely
  const project = booking?.project;
  const property = booking?.property;
  const unit = booking?.unit;
  const sale_order = booking?.sale_order;
  const status = booking?.status;
  const status_verbose = booking?.status_verbose;
  const agent = booking?.agent;

  // Fetch project units when dialog opens
  useEffect(() => {
    if (addProjectUnitDialogOpen && booking) {
      const fetchData = async () => {
        try {
          setFetchingData(true);
          const data = await fetchProjectUnitSelect();
          // The API returns projects with nested units
          const projects = data || [];
          setProjectUnits(projects);
          
          // Pre-select if project/unit already exists
          if (project) {
            const existingProject = projects.find(p => 
              p.id === project.id || 
              p.value === project.id ||
              (typeof p === 'object' && (p.id === project.id || p.value === project.id))
            );
            if (existingProject) {
              setSelectedProject(existingProject);
              if (unit && existingProject.units) {
                const existingUnit = existingProject.units.find(u => 
                  u.id === unit.id || 
                  u.value === unit.id ||
                  (typeof u === 'object' && (u.id === unit.id || u.value === unit.id))
                );
                if (existingUnit) {
                  setSelectedUnit(existingUnit);
                }
              }
            }
          }
        } catch (error) {
          console.error('Error fetching project units:', error);
          openErrorModal('Failed to load projects and units. Please try again.');
        } finally {
          setFetchingData(false);
        }
      };
      fetchData();
    }
  }, [addProjectUnitDialogOpen, booking, project, unit, openErrorModal]);

  // Get filtered units based on selected project
  const filteredUnits = selectedProject ? (selectedProject.units || []) : [];

  // Handle save - using dedicated assign-project-unit endpoint
  const handleSaveProjectUnit = async () => {
    if (!selectedProject) {
      openErrorModal('Please select a project');
      return;
    }

    setLoading(true);
    try {
      const projectId = selectedProject.id || selectedProject.value;
      const unitId = selectedUnit ? (selectedUnit.id || selectedUnit.value) : null;

      await assignProjectUnit(booking.id, projectId, unitId);
      openSuccessModal('Project and unit added successfully!');
      closeAddUnitDialog();
      setSelectedProject(null);
      setSelectedUnit(null);
      
      if (onActionComplete) {
        onActionComplete();
      }
    } catch (error) {
      console.error('Error updating booking:', error);
      openErrorModal('Failed to add project and unit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    closeAddUnitDialog();
    setSelectedProject(null);
    setSelectedUnit(null);
  };

  if (!booking) return null;

  const handleOtherChargesClick = (event) => {
    setOtherChargesAnchor(event.currentTarget);
  };

  const handleOtherChargesClose = () => {
    setOtherChargesAnchor(null);
  };

  return (
    <Stack spacing={0} sx={{ overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
      {/* Project card - reference: gradient header, project name, developer */}
      <Box sx={{ p: '18px 18px 14px', borderBottom: `1px solid ${bookingTokens.borderL}`, background: 'linear-gradient(180deg,#f4faf7 0%,#fff 100%)' }}>
        <Box sx={{ width: '100%', height: 96, borderRadius: 1.25, mb: 1.75, background: 'linear-gradient(135deg,#d1fae5,#a7f3d0)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, border: `1px solid ${bookingTokens.border}` }}>
          {project?.thumbnail ? renderThumbnail(project.thumbnail, 'small') : <ApartmentIcon sx={{ fontSize: 48, color: bookingTokens.green, opacity: 0.7 }} />}
        </Box>
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: bookingTokens.ink, letterSpacing: '-0.3px', mb: 0.5 }}>
          {project?.name || (booking.status === 'draft' ? 'Draft - No Project' : 'Project N/A')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
          <Typography component="span" sx={{ fontSize: 11 }}>🏢</Typography>
          <Typography sx={{ fontSize: 12, color: bookingTokens.subtle }}>Developer: <strong>{project?.developer_name || 'Not Available'}</strong></Typography>
        </Box>
        {(!project || !unit) && booking.status === 'draft' && (
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${bookingTokens.borderL}` }}>
            <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={openAddUnitDialog} sx={{ py: 1.2, fontWeight: 600, textTransform: 'none', background: `linear-gradient(135deg,${bookingTokens.green},${bookingTokens.greenMd})`, color: '#fff', boxShadow: '0 2px 10px rgba(10,107,62,0.25)', '&:hover': { background: `linear-gradient(135deg,${bookingTokens.greenMd},${bookingTokens.green})` } }}>
              {!project && !unit ? 'Add Project & Unit' : !project ? 'Add Project' : 'Add Unit'}
            </Button>
          </Box>
        )}
      </Box>

      {/* Customer Payables - reference: base, other, total in green box */}
      <Box sx={{ p: '14px 18px', borderBottom: `1px solid ${bookingTokens.borderL}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
          <Typography component="span" sx={{ fontSize: 11.5 }}>₹</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: bookingTokens.green, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Customer Payables</Typography>
        </Box>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Box sx={{ flex: 1, bgcolor: bookingTokens.bg, borderRadius: 1.125, p: '9px 11px', border: `1px solid ${bookingTokens.border}` }}>
              <Typography sx={{ fontSize: 9, fontWeight: 700, color: bookingTokens.muted, textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.375 }}>Base Price</Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: bookingTokens.ink }}>{formatCurrencyWithLabel(sale_order?.base_price || 0)}</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box onClick={handleOtherChargesClick} sx={{ flex: 1, bgcolor: bookingTokens.bg, borderRadius: 1.125, p: '9px 11px', border: `1px solid ${bookingTokens.border}`, cursor: 'pointer', '&:hover': { bgcolor: bookingTokens.greenSf } }}>
              <Typography sx={{ fontSize: 9, fontWeight: 700, color: bookingTokens.muted, textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.375 }}>Other Charges</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: bookingTokens.ink }}>{formatCurrencyWithLabel(sale_order?.total_other_charges || 0)}</Typography>
                <Tooltip title="View breakdown" arrow><InfoIcon sx={{ fontSize: 12, color: bookingTokens.muted }} onClick={(e) => { e.stopPropagation(); handleOtherChargesClick(e); }} /></Tooltip>
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Box sx={{ background: `linear-gradient(135deg,${bookingTokens.green},${bookingTokens.greenMd})`, borderRadius: 1.25, p: '11px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.25 }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>₹ Total</Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 900, color: '#fff', letterSpacing: '-0.3px' }}>{formatCurrencyWithLabel(sale_order?.total_price || 0)}</Typography>
        </Box>
      </Box>

      {/* Unit Details – hero unit number, Property Specs grid, Area Specs grid */}
      {unit ? (
        <Box sx={{ p: '16px 18px', borderBottom: `1px solid ${bookingTokens.borderL}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
            <Typography component="span" sx={{ fontSize: 13 }}>🏠</Typography>
            <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: bookingTokens.green, textTransform: 'uppercase', letterSpacing: '0.9px' }}>Unit Details</Typography>
          </Box>
          {/* Unit Number hero box */}
          <Box sx={{
            background: bookingTokens.greenSf,
            borderRadius: '10px',
            padding: '12px 14px',
            textAlign: 'center',
            marginBottom: '14px',
            border: `1.5px solid ${bookingTokens.border}`,
          }}>
            <Typography sx={{ fontSize: 9, fontWeight: 800, color: bookingTokens.muted, textTransform: 'uppercase', letterSpacing: '0.9px' }}>Unit Name</Typography>
            <Typography sx={{ marginTop: 0.5, fontSize: 26, fontWeight: 900, color: bookingTokens.green, letterSpacing: '-0.5px' }}>
              {project && unit.configuration_id && unit.id ? (
                <Link href={`/inventory/projects/${project.id}/configurations/${unit.configuration_id}/units/${unit.id}`} sx={{ color: bookingTokens.green, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                  {unit.unit_number || unit.name || unit.unit_code || 'N/A'}
                </Link>
              ) : (unit.unit_number || unit.name || unit.unit_code || 'N/A')}
            </Typography>
          </Box>
          {/* Property Specs */}
          <Typography sx={{ fontSize: 9, fontWeight: 800, color: bookingTokens.muted, textTransform: 'uppercase', letterSpacing: '0.9px', display: 'block', mb: 1 }}>Property Specs</Typography>
          <Grid container spacing={1} sx={{ mb: 1.75 }}>
            {[['Block', unit?.block_tower], ['Floor', unit?.floor], ['BHK', unit?.bhk_type]].map(([label, value]) => (
              <Grid item xs={4} key={label}>
                <Box sx={{
                  textAlign: 'center',
                  background: bookingTokens.bg,
                  borderRadius: '8px',
                  padding: '8px 6px',
                  border: `1px solid ${bookingTokens.borderL}`,
                  height: '100%',
                }}>
                  <Typography sx={{ fontSize: 9, fontWeight: 800, color: bookingTokens.muted, textTransform: 'uppercase', letterSpacing: '0.9px' }}>{label}</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: label === 'BHK' ? bookingTokens.green : bookingTokens.ink, mt: 0.375 }}>{value || 'N/A'}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          {/* Area Specs */}
          <Typography sx={{ fontSize: 9, fontWeight: 800, color: bookingTokens.muted, textTransform: 'uppercase', letterSpacing: '0.9px', display: 'block', mb: 1 }}>Area Specs</Typography>
          <Grid container spacing={1}>
            {[['Super BUA', unit?.super_built_up_area ? `${unit.super_built_up_area.toLocaleString('en-IN')} ${unit.measurement_unit || ''}`.trim() : null], ['Facing', unit?.facing], ['Built-up', unit?.built_up_area ? `${unit.built_up_area.toLocaleString('en-IN')} ${unit.measurement_unit || ''}`.trim() : null], ['Carpet', unit?.carpet_area ? `${unit.carpet_area.toLocaleString('en-IN')} ${unit.measurement_unit || ''}`.trim() : null]].map(([label, value]) => (
              <Grid item xs={6} key={label}>
                <Box sx={{
                  background: bookingTokens.bg,
                  borderRadius: '8px',
                  padding: '9px 10px',
                  border: `1px solid ${bookingTokens.borderL}`,
                  height: '100%',
                }}>
                  <Typography sx={{ fontSize: 9, fontWeight: 800, color: bookingTokens.muted, textTransform: 'uppercase', letterSpacing: '0.9px' }}>{label}</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: bookingTokens.ink2, mt: 0.375 }}>{value || 'N/A'}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <Box sx={{ p: '16px 18px', borderBottom: `1px solid ${bookingTokens.borderL}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
            <Typography component="span" sx={{ fontSize: 13 }}>🏠</Typography>
            <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: bookingTokens.green, textTransform: 'uppercase', letterSpacing: '0.9px' }}>Unit Details</Typography>
          </Box>
          <Box sx={{
            textAlign: 'center',
            padding: '20px 0',
            background: bookingTokens.bg,
            borderRadius: '10px',
            border: `1.5px dashed ${bookingTokens.border}`,
          }}>
            <Typography sx={{ fontSize: 12, color: bookingTokens.muted, fontStyle: 'italic' }}>No unit assigned yet.</Typography>
          </Box>
        </Box>
      )}

      {/* Assigned Agent - reference */}
      <Box sx={{ p: '14px 18px', flex: 1 }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: bookingTokens.muted, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.25 }}>Assigned Agent</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '50%', background: `linear-gradient(135deg,${bookingTokens.green},#12a85e)`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>
            {agent?.name ? agent.name.trim().split(/\s+/).map((n) => n[0]).join('').toUpperCase().slice(0, 2) : '?'}
          </Box>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: bookingTokens.ink }}>{agent?.name || 'Not assigned'}</Typography>
            <Typography sx={{ fontSize: 11, color: bookingTokens.muted, mt: 0.25 }}>{agent?.phone || '—'}</Typography>
          </Box>
        </Box>
      </Box>

      {/* Other Charges Popover */}
      <Popover
        open={Boolean(otherChargesAnchor)}
        anchorEl={otherChargesAnchor}
        onClose={handleOtherChargesClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: 400 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Other Charges Breakdown
          </Typography>
          {sale_order?.other_charges && sale_order.other_charges.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Charge Type</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sale_order.other_charges.map((charge, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{charge.name}</Typography>
                      {charge.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {charge.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        ₹{Number(charge.amount).toLocaleString('en-IN')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    ₹{Number(sale_order?.total_other_charges || 0).toLocaleString('en-IN')}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No other charges added.
            </Typography>
          )}
        </Box>
      </Popover>

      {/* Add Project & Unit Dialog */}
      <Dialog 
        open={addProjectUnitDialogOpen} 
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ApartmentIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Add Project & Unit
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select a project and optionally a unit to complete your booking details.
            </Typography>

            {fetchingData ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  Loading projects and units...
                </Typography>
              </Box>
            ) : (
              <>
                {/* Project Selection */}
                <Autocomplete
                  value={selectedProject}
                  onChange={(_, newValue) => {
                    setSelectedProject(newValue);
                    setSelectedUnit(null); // Reset unit when project changes
                  }}
                  options={projectUnits}
                  getOptionLabel={(option) => option.label || option.name || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Project"
                      required
                      helperText="Project is required to proceed"
                      sx={{ mb: 2 }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => 
                    (option.id || option.value) === (value?.id || value?.value)
                  }
                  disabled={fetchingData}
                />

                {/* Unit Selection - Only show if project is selected */}
                {selectedProject && (
                  <Autocomplete
                    value={selectedUnit}
                    onChange={(_, newValue) => setSelectedUnit(newValue)}
                    options={filteredUnits}
                    getOptionLabel={(option) => option.label || option.name || option.unit_number || ''}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Unit (Optional)"
                        helperText="Unit can be added later if not selected now"
                      />
                    )}
                    isOptionEqualToValue={(option, value) => 
                      (option.id || option.value) === (value?.id || value?.value)
                    }
                    disabled={fetchingData}
                  />
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={handleDialogClose} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleSaveProjectUnit} 
            variant="contained" 
            disabled={!selectedProject || loading}
            startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {loading ? 'Saving...' : 'Add to Booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default BookingSidebar;
